import { useEffect, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { api } from '@/lib/api';
import { useMonthStore } from '@/lib/monthStore';
import { usePeriodStore, monthIdsQuery } from '@/lib/periodStore';
import { GlassCard } from '@/components/ui/GlassCard';
import { PageHeader } from '@/components/ui/PageHeader';
import { KpiCard } from '@/components/ui/KpiCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/cn';
import {
  formatCurrency,
  formatCurrencyCompact,
  formatNumber,
  formatPercent,
} from '@/lib/format';
import { Link } from 'react-router-dom';
import type {
  AggregatedKpisResponse,
  KpiCard as KpiCardType,
  PaymentChannel,
  PmixItem,
  PmixResponse,
  SalesTrendPoint,
} from '@/types';

const CHANNEL_COLORS: Record<string, string> = {
  tarjeta: '#ff8a65',
  efectivo: '#22d3ee',
  rappi: '#a3e635',
  uber: '#fb923c',
};

const CHANNEL_ICONS: Record<string, string> = {
  tarjeta: 'credit_card',
  efectivo: 'payments',
  rappi: 'delivery_dining',
  uber: 'electric_moped',
};

const CHANNEL_LABELS: Record<string, string> = {
  tarjeta: 'Tarjeta',
  efectivo: 'Efectivo',
  rappi: 'Rappi',
  uber: 'Uber Eats',
};

const TOOLTIP_STYLE = {
  background: '#0a0a0a',
  border: '2px solid #ff8a65',
  borderRadius: 0,
  boxShadow: '4px 4px 0 0 #000000',
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '12px',
  color: '#f5f5f5',
};

export default function Dashboard() {
  const selectedMonthId = useMonthStore((s) => s.selectedMonthId);
  const monthIds = usePeriodStore((s) => s.monthIds);
  const isMultiMonth = monthIds.length > 1;

  const [kpis, setKpis] = useState<KpiCardType[]>([]);
  const [trend, setTrend] = useState<SalesTrendPoint[]>([]);
  const [pmix, setPmix] = useState<PmixItem[]>([]);
  const [channels, setChannels] = useState<PaymentChannel[]>([]);
  const [excludedMonths, setExcludedMonths] = useState<
    Array<{ monthId: string; fileName: string; reason: string }>
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (monthIds.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setExcludedMonths([]);

    if (isMultiMonth) {
      const q = monthIdsQuery(monthIds);
      Promise.all([
        api.get<AggregatedKpisResponse>(`/aggregate/kpis?monthIds=${q}`),
        api.get<PmixResponse>(`/aggregate/pmix?monthIds=${q}`),
      ])
        .then(([kpisRes, pmixRes]) => {
          setExcludedMonths(kpisRes.excluded);
          setKpis(buildAggregatedKpis(kpisRes.kpis));
          setPmix(
            pmixRes.pmix.map((p) => ({
              code: p.code,
              name: p.name,
              units: p.totalUnits,
              revenue: p.totalRevenue,
              participation: p.participation,
              flavorVelocity: 0,
              status:
                p.trend === 'UP'
                  ? 'STAR'
                  : p.trend === 'DOWN'
                    ? 'DOG'
                    : p.trend === 'NEW'
                      ? 'STAR'
                      : 'CASH_COW',
            })),
          );
          if (selectedMonthId && monthIds.includes(selectedMonthId)) {
            Promise.all([
              api.get<SalesTrendPoint[]>(`/sales/month/${selectedMonthId}/trend`),
              api.get<PaymentChannel[]>(`/sales/payment-channels/${selectedMonthId}`),
            ])
              .then(([t, ch]) => {
                setTrend(t);
                setChannels(ch);
              })
              .catch(() => {});
          } else {
            setTrend([]);
            setChannels([]);
          }
        })
        .catch((err) => {
          console.error('Error cargando dashboard agregado:', err);
        })
        .finally(() => setLoading(false));
    } else {
      const id = selectedMonthId || monthIds[0];
      if (!id) {
        setLoading(false);
        return;
      }
      Promise.all([
        api.get<KpiCardType[]>(`/metrics/month/${id}/kpis`),
        api.get<SalesTrendPoint[]>(`/sales/month/${id}/trend`),
        api.get<PmixItem[]>(`/sales/month/${id}/pmix`),
        api.get<PaymentChannel[]>(`/sales/payment-channels/${id}`),
      ])
        .then(([kpisData, trendData, pmixData, channelsData]) => {
          setKpis(kpisData);
          setTrend(trendData);
          setPmix(pmixData);
          setChannels(channelsData);
        })
        .catch((err) => {
          console.error('Error cargando dashboard:', err);
        })
        .finally(() => setLoading(false));
    }
  }, [selectedMonthId, monthIds, isMultiMonth]);

  if (monthIds.length === 0) {
    return (
      <>
        <PageHeader
          tag="// MÓDULO :: 01"
          title="Dashboard"
          subtitle="Resumen de ventas, mix de productos y canales de pago"
        />
        <EmptyState
          icon="calendar_month"
          title="Sin datos cargados"
          description="Carga un archivo Excel para ver las métricas del mes."
          action={
            <Link to="/upload" className="kinetic-button-cyan">
              <span className="material-symbols-outlined">cloud_upload</span>
              Cargar archivo
            </Link>
          }
        />
      </>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <span className="material-symbols-outlined text-5xl text-primary animate-spin">
            progress_activity
          </span>
          <p className="font-mono uppercase tracking-widest text-xs text-secondary">
            // Sincronizando datos
          </p>
        </div>
      </div>
    );
  }

  const pieData = pmix.slice(0, 6).map((p) => ({
    name: p.code,
    value: p.units,
    fullName: p.name,
  }));

  return (
    <>
      <PageHeader
        tag="// MÓDULO :: 01 / VENTAS"
        title="Dashboard"
        subtitle={
          isMultiMonth
            ? `Vista multi-mes · ${monthIds.length} meses · mix, KPIs y canales agregados`
            : 'Resumen de ventas, mix de productos y canales de pago'
        }
      />

      {/* Banner de meses excluidos */}
      {excludedMonths.length > 0 && (
        <div className="mb-6 bg-surface-container border-2 border-warning shadow-[3px_3px_0_0_#000000] p-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-warning mt-0.5">warning</span>
          <div className="flex-1">
            <p className="font-bold text-text-bright font-mono uppercase tracking-widest text-sm">
              ⚠ {excludedMonths.length} mes(es) excluido(s) por baja calidad
            </p>
            <ul className="text-text-muted mt-2 text-xs space-y-1 list-disc list-inside font-mono">
              {excludedMonths.map((m) => (
                <li key={m.monthId}>
                  {m.fileName} — {m.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* KPI Row */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        {kpis.slice(0, 4).map((kpi) => (
          <KpiCard key={kpi.key} kpi={kpi} />
        ))}
      </section>

      <div className="grid grid-cols-12 gap-5 mb-6">
        {/* Daily Sales Evolution */}
        <GlassCard
          variant="kinetic"
          padding="md"
          rounded="none"
          className="col-span-12 lg:col-span-8"
        >
          <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-outline">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                // DAILY_EVOLUTION
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Ventas Diarias
              </h3>
            </div>
            <div className="flex items-center gap-2 px-2 py-1 border-2 border-outline bg-surface font-mono text-[10px] uppercase tracking-widest text-primary">
              <span className="w-2 h-2 bg-primary" />
              Ingresos
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="colorNsr" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ff8a65" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#ff8a65" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis
                dataKey="dayOfMonth"
                stroke="#9ca3af"
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: '#525252' }}
              />
              <YAxis
                stroke="#9ca3af"
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: '#525252' }}
                tickFormatter={(v) => formatCurrencyCompact(v)}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(v: number) => [formatCurrency(v), 'Ingresos']}
                labelFormatter={(label) => `Día ${label}`}
              />
              <Area
                type="monotone"
                dataKey="nsr"
                stroke="#ff8a65"
                strokeWidth={3}
                fill="url(#colorNsr)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </GlassCard>

        {/* Product Mix — Donut */}
        <GlassCard
          variant="kinetic"
          padding="md"
          rounded="none"
          className="col-span-12 lg:col-span-4"
        >
          <div className="mb-4 pb-3 border-b-2 border-outline">
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
              // PRODUCT_MIX
            </p>
            <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
              Mix de Sabores
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={85}
                paddingAngle={2}
                dataKey="value"
                stroke="#0a0a0a"
                strokeWidth={2}
              >
                {pieData.map((_, idx) => (
                  <Cell
                    key={idx}
                    fill={
                      ['#ff8a65', '#22d3ee', '#a3e635', '#fb923c', '#c084fc', '#fbbf24'][
                        idx
                      ]
                    }
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(v: number, _name, p) => [
                  formatNumber(v),
                  (p as { payload: { fullName: string } }).payload.fullName,
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2 max-h-32 overflow-y-auto custom-scrollbar">
            {pmix.slice(0, 6).map((p, i) => (
              <div key={p.code} className="flex justify-between items-center text-xs">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2 h-2"
                    style={{
                      background:
                        ['#ff8a65', '#22d3ee', '#a3e635', '#fb923c', '#c084fc', '#fbbf24'][
                          i
                        ],
                    }}
                  />
                  <span className="font-mono uppercase tracking-wider text-text-bright">
                    {p.code}
                  </span>
                  <span className="text-text-muted">{p.name}</span>
                </div>
                <span className="font-bold num text-primary">
                  {formatPercent(p.participation)}
                </span>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      {/* Payment Channels */}
      <GlassCard variant="kinetic" padding="md" rounded="none">
        <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-outline">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
              // CHANNELS
            </p>
            <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
              Canales de Pago
            </h3>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {channels.map((ch) => (
            <div
              key={ch.channel}
              className={cn(
                'bg-surface border-2 border-outline p-4 cursor-pointer',
                'shadow-[3px_3px_0_0_#000000]',
                'transition-all duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[5px_5px_0_0_#ff8a65]',
              )}
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 flex items-center justify-center border-2 border-black text-black"
                  style={{ background: CHANNEL_COLORS[ch.channel] }}
                >
                  <span className="material-symbols-outlined text-xl">
                    {CHANNEL_ICONS[ch.channel]}
                  </span>
                </div>
                <div>
                  <p className="font-bold uppercase tracking-widest text-xs text-text-bright">
                    {CHANNEL_LABELS[ch.channel]}
                  </p>
                  <p className="font-mono text-[10px] text-text-muted num">
                    {formatPercent(ch.participation)}
                  </p>
                </div>
              </div>
              <p className="font-condensed text-3xl text-text-bright num leading-none">
                {formatCurrency(ch.total)}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-2 num">
                AVG {formatCurrency(ch.dailyAvg)}/d
              </p>
            </div>
          ))}
        </div>
      </GlassCard>
    </>
  );
}

function buildAggregatedKpis(agg: AggregatedKpisResponse['kpis']): KpiCardType[] {
  return [
    {
      key: 'tus',
      label: 'Unidades vendidas (TUS)',
      value: agg.totalUnits,
      unit: 'unidades',
      target: agg.totalUnits * 0.9,
      status: 'GOOD',
      format: 'number',
    },
    {
      key: 'nsr',
      label: 'Ingresos netos (NSR)',
      value: agg.totalRevenue,
      unit: 'MXN',
      target: null,
      status: 'NEUTRAL',
      format: 'currency',
    },
    {
      key: 'avg-daily-units',
      label: 'Promedio diario',
      value: agg.avgDailyUnits,
      unit: 'unidades/día',
      target: null,
      status: 'NEUTRAL',
      format: 'number',
    },
    {
      key: 'avg-daily-rev',
      label: 'Ingreso promedio diario',
      value: agg.avgDailyRevenue,
      unit: 'MXN/día',
      target: null,
      status: 'NEUTRAL',
      format: 'currency',
    },
  ];
}
